import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ObjectSchema } from 'yup';
import { formAutocomplete } from '../../../config';
import { FormHelpers } from '../../../screens/utils';
import { useFlowTypeChecker } from '../../../services/common/miscUtils';
import { journeyDataActions } from '../../../services/redux/features/journeyData.slice';
import { AddressSearchFormField } from '../../Fieldset/AddressSearchFormField';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from './formInputs';
import { AddressFormModel_MX } from './formModel';
import { getInitialValues } from './initialValues';
import { requestMapper } from './requestMapper';
import { getValidationSchema } from './validationSchema';

type Props = {
  formHelpers: FormHelpers;
  onValuesChange?: (values: AddressFormModel_MX) => void;
  isSecondHirer?: boolean;
};

export const AddressFormNew_MX: React.FC<Props> = (props) => {
  const flowTypeIs = useFlowTypeChecker();
  const isConsumerOrSoleTrader = flowTypeIs('consumer') || flowTypeIs('soleTrader');
  const { formHelpers } = props;
  const [initialValues] = useState(getInitialValues(isConsumerOrSoleTrader));
  const [validationSchema] = useState<ObjectSchema<Partial<AddressFormModel_MX>>>(
    getValidationSchema(isConsumerOrSoleTrader)
  );

  const { formSectionProps, onFormSubmitSuccess, onStartEditInSummaryMode, isDisabledFormSummary } = formHelpers;

  const onSubmit = (values: AddressFormModel_MX) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  const titlePath = flowTypeIs('consumer') ? 'private' : 'business';

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(`addressForm:title:${titlePath}`), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent {...props} isConsumerOrSoleTrader={isConsumerOrSoleTrader} />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = Props & {
  isConsumerOrSoleTrader: boolean;
};

export const FormContent = (props: FormContentProps) => {
  const formTranslationKey = 'addressForm:currentAddressForm';
  const [inputs] = useState(createFormInputs(formTranslationKey));

  const { values, submitForm } = useFormikContext<AddressFormModel_MX>();
  const { onValuesChange, formHelpers, isConsumerOrSoleTrader, isSecondHirer } = props;
  const { onFormValuesChange } = formHelpers;

  const dispatch = useDispatch();

  useEffect(() => {
    onValuesChange?.(values);
    dispatch(journeyDataActions.updateData(requestMapper(values, isSecondHirer)));
    onFormValuesChange(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, onValuesChange, onFormValuesChange]);

  const disableForm = formHelpers.isDisabledFormSummary;

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>


      <Fieldset disabled={disableForm}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/1" s="1/1">
              <AddressSearchFormField />
            </Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine1()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine2()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.addressLine3()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.town()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.county()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.eirCode()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        {isConsumerOrSoleTrader &&
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="1/4" m="1/3" s="1/1">{inputs.residentialStatus()}</Layout.Item>
              <Layout.Item default="1/4" m="1/3" s="1/1">{inputs.timeAtAddressFrom(values.timeAtAddressTo!)}</Layout.Item>
              <Layout.Item default="1/4" m="1/3" s="1/1">{inputs.timeAtAddressTo()}</Layout.Item>
            </Layout>
          </Fieldset.Row>
        }

      </Fieldset>

      <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers}/>
    </Form>
  );
};
